import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { SocialIcons } from "@/components/SocialLinks";
import { useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import SEOHead from "@/components/SEOHead";
import ContactForm from "@/components/ContactForm";
import PaymentMethods from "@/components/PaymentMethods";
import GoogleTrustPill from "@/components/GoogleTrustPill";
import WhatsAppButton from "@/components/WhatsAppButton";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { whatsAppUrl, whatsAppReplyPromise, WHATSAPP_NUMBER_DISPLAY } from "@/utils/whatsapp";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);
  const hasProduct = !!searchParams.get("product");

  useEffect(() => {
    if (hasProduct && formRef.current) {
      setTimeout(() => {
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [hasProduct]);

  return (
    <div>
      <SEOHead title="Contact Us | Get Card Machines and EPOS Systems" description="Need expert advice on card machines and EPOS systems? Contact the CanDo Payments today for reliable card payment solutions tailored to your needs." path="/contact" contactPage />
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center max-w-3xl mx-auto">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Contact Us</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight mb-6">Let&apos;s have a <span className="gradient-text">conversation</span></motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed">No hard sell. No jargon. Just a friendly chat about how we can help your business take smarter payments.</motion.p>
          </motion.div>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-2xl font-bold text-brand-black mb-6">Get in touch</h2>
              <div className="space-y-6">
                {[
                  { icon: Phone, label: "Call us free", value: "0800 368 2989", href: "tel:+448003682989" },
                  { icon: WhatsAppIcon, label: "WhatsApp us", value: WHATSAPP_NUMBER_DISPLAY, href: whatsAppUrl("contact"), external: true, hint: whatsAppReplyPromise() },
                  { icon: Mail, label: "Email us", value: "hello@CanDoPayments.com", href: "mailto:hello@CanDoPayments.com" },
                  { icon: MapPin, label: "Visit us", value: "The Technology Centre, Inward Way, Ellesmere Port, CH65 3EN", isAddress: true },
                  { icon: Clock, label: "Office hours", value: "Monday — Friday, 9am — 5pm" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0">
                      <item.icon size={18} className="text-brand-aqua" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-brand-grey mb-0.5">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                          className="text-brand-black font-semibold hover:text-brand-aqua transition-colors"
                          data-testid={`contact-${item.label.replace(/\s/g, '-').toLowerCase()}`}
                        >
                          {item.value}
                        </a>
                      ) : item.isAddress ? (
                        <address className="text-brand-black font-semibold not-italic">{item.value}</address>
                      ) : (
                        <p className="text-brand-black font-semibold">{item.value}</p>
                      )}
                      {item.hint && <p className="text-xs text-brand-grey mt-0.5">{item.hint}</p>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="/book" className="inline-flex items-center gap-2 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm transition-colors" data-testid="contact-book-call">Book a Call Online</a>
                <WhatsAppButton variant="secondary" placement="contact" label="Or WhatsApp us" />
              </div>
              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-grey mb-2.5">Follow us</p>
                <SocialIcons variant="contact" />
              </div>
              <div className="mt-6 rounded-2xl overflow-hidden border border-brand-light-grey h-64">
                <iframe src="https://maps.google.com/maps?q=CanDo+Payments,+The+Technology+Centre,+Inward+Way,+Ellesmere+Port,+CH65+3EN&z=15&output=embed" width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" title="CanDo Payments — Ellesmere Port office location" data-testid="contact-map" />
              </div>
            </div>
            <div ref={formRef} className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm h-fit scroll-mt-24">
              <h3 className="font-heading text-xl font-bold text-brand-black mb-2">Send us a message</h3>
              <p className="text-sm text-brand-grey mb-2">Fill in the form and one of our friendly team will get back to you — usually within a few hours.</p>
              <p className="text-sm text-brand-grey mb-5"><em>In a hurry? <a href={whatsAppUrl("contact")} target="_blank" rel="noopener noreferrer" className="text-brand-aqua font-semibold hover:text-brand-aqua-dark">WhatsApp us</a> instead — we typically reply in under 4 minutes during office hours.</em></p>
              <div className="mb-5">
                <GoogleTrustPill reassurance="We reply in under 2 business hours" testId="contact-trust-pill" />
              </div>
              <ContactForm />
              <div className="mt-6 pt-6 border-t border-brand-light-grey/50">
                <PaymentMethods size="sm" showLabel={false} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
